/**
 * Invitations -- 내 청첩장 목록
 */

import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Heart, Plus, Send, Eye, Users, MessageSquare, Link2, Copy, Check } from 'lucide-react'
import { useApiFetch } from '../../hooks/useApiFetch.js'
import { Button, Spinner, EmptyState, Badge, StatCard } from '../../components/ui/index.js'
import { CreateInvitationModal } from '../../components/modals/CreateInvitationModal.js'
import { SetSlugModal } from '../../components/modals/SetSlugModal.js'
import styles from './Invitations.module.css'

interface RsvpSummary {
  total: number
  attending: number
  notAttending: number
  maybe: number
  totalGuests: number
}

interface GuestbookSummary {
  total: number
}

interface InvitationSummary {
  id: string
  slug: string | null
  groomName: string
  brideName: string
  weddingDate: string
  venueName: string
  aiPhotoUrl: string | null
  galleryImages?: string[] | null
  data?: {
    gallery?: { coverImage?: string; images?: string[] }
    aiPhotoUrl?: string
  }
  extendedData?: {
    gallery?: { coverImage?: string }
  }
  status: 'DRAFT' | 'PUBLISHED' | 'EXPIRED' | 'DELETED'
  viewCount: number
  createdAt: string
  rsvpSummary?: RsvpSummary
  guestbookSummary?: GuestbookSummary
}

function getCoverImage(inv: InvitationSummary): string | undefined {
  return inv.data?.gallery?.coverImage
    ?? inv.extendedData?.gallery?.coverImage
    ?? inv.data?.aiPhotoUrl
    ?? inv.aiPhotoUrl
    ?? inv.data?.gallery?.images?.[0]
    ?? inv.galleryImages?.[0]
    ?? undefined
}

interface InvitationsResponse {
  invitations: InvitationSummary[]
  pagination: { total: number }
}

const STATUS_LABEL: Record<string, string> = {
  DRAFT: '작성 중',
  PUBLISHED: '발행됨',
  EXPIRED: '만료',
}

function formatDate(dateStr: string) {
  const d = new Date(dateStr)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}.${m}.${day}`
}

export function Invitations() {
  const navigate = useNavigate()
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [slugModalTarget, setSlugModalTarget] = useState<InvitationSummary | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const { data, isLoading, refetch } = useApiFetch<InvitationsResponse>('/api/invitations')

  const invitations = data?.invitations ?? []

  const totalCount = invitations.length
  const publishedCount = invitations.filter(inv => inv.status === 'PUBLISHED').length
  const totalViews = invitations.reduce((sum, inv) => sum + (inv.viewCount ?? 0), 0)
  const totalAttending = invitations.reduce((sum, inv) => sum + (inv.rsvpSummary?.attending ?? 0), 0)
  const totalGuestbook = invitations.reduce((sum, inv) => sum + (inv.guestbookSummary?.total ?? 0), 0)

  if (isLoading) {
    return (
      <div className={styles.root}>
        <div className={styles.loadingRow}><Spinner size="lg" /></div>
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h1 className={styles.title}>내 청첩장</h1>
        <Button onClick={() => setCreateModalOpen(true)}>
          <Plus size={16} />
          새 청첩장 만들기
        </Button>
      </div>

      {invitations.length === 0 ? (
        <EmptyState
          icon={<Heart size={32} strokeWidth={1.5} />}
          title="아직 청첩장이 없어요"
          description="새 청첩장을 만들어보세요"
          action={
            <Button onClick={() => setCreateModalOpen(true)}>
              <Plus size={16} />
              새 청첩장 만들기
            </Button>
          }
        />
      ) : (
        <>
          <div className={styles.statsRow}>
            <StatCard
              icon={<Heart size={18} strokeWidth={1.5} />}
              label="총 청첩장"
              value={totalCount}
            />
            <StatCard
              icon={<Send size={18} strokeWidth={1.5} />}
              label="발행됨"
              value={publishedCount}
            />
            <StatCard
              icon={<Eye size={18} strokeWidth={1.5} />}
              label="총 조회수"
              value={totalViews.toLocaleString()}
            />
            <StatCard
              icon={<Users size={18} strokeWidth={1.5} />}
              label="참석 예정"
              value={totalAttending}
            />
            <StatCard
              icon={<MessageSquare size={18} strokeWidth={1.5} />}
              label="방명록"
              value={totalGuestbook}
            />
          </div>

          <div className={styles.grid}>
            {invitations.map(inv => (
              <div
                key={inv.id}
                className={styles.card}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/studio/editor/${inv.id}`)}
                onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/studio/editor/${inv.id}`) }}
              >
                <div className={styles.cardCover}>
                  {getCoverImage(inv) ? (
                    <img src={getCoverImage(inv)} alt="" className={styles.cardCoverImg} />
                  ) : (
                    <Heart size={24} strokeWidth={1.5} />
                  )}
                </div>
                <div className={styles.cardBody}>
                  <div className={styles.cardTitleRow}>
                    <span className={styles.cardTitle}>
                      {inv.groomName} ♥ {inv.brideName}
                    </span>
                    <Badge color={inv.status === 'PUBLISHED' ? 'green' : 'gray'}>
                      {STATUS_LABEL[inv.status] ?? inv.status}
                    </Badge>
                  </div>
                  <span className={styles.cardMeta}>
                    {inv.venueName} · {formatDate(inv.weddingDate)}
                  </span>
                  <span className={styles.cardViews}>
                    <Eye size={14} strokeWidth={1.5} />
                    {inv.viewCount?.toLocaleString() ?? '0'}
                  </span>
                  {inv.status === 'PUBLISHED' && (
                    <span className={styles.cardUrl}>
                      <Link2 size={13} strokeWidth={1.5} />
                      {inv.slug
                        ? <>
                            <span className={styles.slugText}>{window.location.origin}/inv/{inv.slug}</span>
                            <button
                              type="button"
                              className={styles.copyBtn}
                              onClick={(e) => {
                                e.stopPropagation()
                                navigator.clipboard.writeText(`${window.location.origin}/inv/${inv.slug}`)
                                setCopiedId(inv.id)
                                setTimeout(() => setCopiedId(null), 2000)
                              }}
                            >
                              {copiedId === inv.id ? <Check size={12} /> : <Copy size={12} />}
                            </button>
                          </>
                        : <button
                            type="button"
                            className={styles.setSlugLink}
                            onClick={(e) => { e.stopPropagation(); setSlugModalTarget(inv) }}
                          >
                            커스텀 링크 설정
                          </button>
                      }
                    </span>
                  )}
                </div>
                <div className={styles.cardSummary}>
                  <span className={styles.summaryRow}>
                    <Users size={13} strokeWidth={1.5} />
                    참석 {inv.rsvpSummary?.attending ?? 0} · 불참 {inv.rsvpSummary?.notAttending ?? 0} · 미정 {inv.rsvpSummary?.maybe ?? 0}
                    <span className={styles.summaryTotal}>(총 {inv.rsvpSummary?.totalGuests ?? 0}명)</span>
                  </span>
                  <span className={styles.summaryRow}>
                    <MessageSquare size={13} strokeWidth={1.5} />
                    방명록 {inv.guestbookSummary?.total ?? 0}건
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      <CreateInvitationModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
      />
      {slugModalTarget && (
        <SetSlugModal
          open
          onClose={() => setSlugModalTarget(null)}
          invitationId={slugModalTarget.id}
          currentSlug={slugModalTarget.slug}
          onSaved={refetch}
        />
      )}
    </div>
  )
}
